import { useTranslation } from 'react-i18next';
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { getMonthlyHoursReport } from '../../../services/reports.service';

const useHoursReportExport = ({ year, atcu }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const fetchMonthlyHoursReportData = async (dateFrom, dateTo) => {
    try {
      const res = await getMonthlyHoursReport(dateFrom, dateTo, atcu?.toLowerCase());
      return res?.data;
    } catch (e) {
      console.log(e);
    }
  };

  const getExcelColumnLabel = (number) => {
    if (!Number.isInteger(number) || number < 1) {
      return null;
    }

    let label = '';
    while (number > 0) {
      number -= 1;
      const remainder = number % 26;
      label = String.fromCharCode(65 + remainder) + label;
      number = Math.floor(number / 26);
    }
    return label;
  }

  const mapHeadingRows = (monthOrdinal, monthYear, startDay, endDay, reportType) => {
    const headingRows = [
      ['IME I PREZIME'],
      [reportType === 'MONTHLY' ? t('monthlyPlan.' + monthOrdinal) : '']
    ];

    if (reportType === 'MONTHLY') {
      for (let i = startDay; i <= endDay; i += 1) {
        const date = new Date(year, monthOrdinal - 1, i);
        const dayLabel = t('daysInWeek.' + new Date(date).getDay()).substring(0, 3).toLowerCase();
        headingRows[0].push(i);
        headingRows[1].push(dayLabel)
      }
    }

    return headingRows;
  };

  const mapExportData = (reportData, reportType) => {
    const sectorRows = [];
    const asseRows = [];
    const ojtiRows = [];
    const managerRows = [];

    const emptyRow = ['', ''];

    const startDay = new Date(reportData?.dateFrom)?.getDate();
    const endDay = new Date(reportData?.dateTo)?.getDate();

    reportData?.sector?.forEach((el, elIndex) => {
      const sectorRow = [
        el?.firstName + ' ' + el?.lastName
      ];
      if (reportType === 'MONTHLY') {
        for (let i = startDay; i <= endDay; i += 1) {
          if (elIndex === 0) emptyRow.push('');
          const assignmentForThatDay = el?.assignments?.find((el) => el?.timeUnit === i);
          if (assignmentForThatDay) {
            const totalHoursCount = assignmentForThatDay?.exe + assignmentForThatDay?.plc;
            sectorRow.push(totalHoursCount);
          } else sectorRow.push('');
        }
      }

      sectorRow.push(el?.plcTotal + el?.exeTotal);
      sectorRows.push(sectorRow);
    });

    reportData?.asse?.forEach((el) => {
      const asseRow = [
        el?.firstName + ' ' + el?.lastName
      ];
      if (reportType === 'MONTHLY') {
        for (let i = startDay; i <= endDay; i += 1) {
          const assignmentForThatDay = el?.assignments?.find((el) => el?.timeUnit === i);
          if (assignmentForThatDay) {
            const totalHoursCount = assignmentForThatDay?.exe + assignmentForThatDay?.plc;
            asseRow.push(totalHoursCount);
          } else asseRow.push('');
        }
      }

      asseRow.push(el?.plcTotal + el?.exeTotal);
      asseRows.push(asseRow);
    });

    reportData?.ojti?.forEach((el) => {
      const ojtiRow = [
        el?.firstName + ' ' + el?.lastName
      ];
      if (reportType === 'MONTHLY') {
        for (let i = startDay; i <= endDay; i += 1) {
          const assignmentForThatDay = el?.assignments?.find((el) => el?.timeUnit === i);
          if (assignmentForThatDay) {
            const totalHoursCount = assignmentForThatDay?.exe + assignmentForThatDay?.plc;
            ojtiRow.push(totalHoursCount);
          } else ojtiRow.push('');
        }
      }

      ojtiRow.push(el?.plcTotal + el?.exeTotal);
      ojtiRows.push(ojtiRow);
    });

    reportData?.manager?.forEach((el) => {
      const managerRow = [
        el?.firstName + ' ' + el?.lastName
      ];
      if (reportType === 'MONTHLY') {
        for (let i = startDay; i <= endDay; i += 1) {
          const assignmentForThatDay = el?.assignments?.find((el) => el?.timeUnit === i);
          if (assignmentForThatDay) {
            const totalHoursCount = assignmentForThatDay?.exe + assignmentForThatDay?.plc;
            managerRow.push(totalHoursCount);
          } else managerRow.push('');
        }
      }

      managerRow.push(el?.plcTotal + el?.exeTotal);
      managerRows.push(managerRow);
    });

    if (sectorRows?.length === 0) sectorRows.push(emptyRow);
    if (asseRows?.length === 0) asseRows.push(emptyRow);
    if (ojtiRows?.length === 0) ojtiRows.push(emptyRow);
    if (managerRows?.length === 0) managerRows.push(emptyRow);

    return {
      sectorRows,
      asseRows,
      ojtiRows,
      managerRows
    }
  };

  function getMonthOrdinals(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start) || isNaN(end) || start > end) {
      return [];
    }

    const months = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);

    while (current <= end) {
      months.push({
        year: current.getFullYear(),
        month: current.getMonth() + 1
      });
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  }

  const getHoursReport = async (dateFrom, dateTo) => {
    setLoading(true);
    const res = await fetchMonthlyHoursReportData(dateFrom, dateTo, atcu);
    const mappedData = [];
    const totalData = [];
    res?.forEach((el) => {
      if (el?.type === 'MONTHLY') {
        mappedData.push(mapExportData(el, 'MONTHLY'));
      } else if (el?.type === 'YEARLY') {
        totalData.push(mapExportData(el, 'YEARLY'));
      }
    });
    const monthOrdinals = getMonthOrdinals(dateFrom, dateTo);

    XlsxPopulate.fromBlankAsync().then(async (workbook) => {
      mappedData?.forEach((el, index) => {
        const { sectorRows, asseRows, ojtiRows, managerRows } = el;

        const iterationMonth = monthOrdinals[index]?.month;
        const iterationYear = monthOrdinals?.[index]?.year;
        let iterationStartDay;
        let iterationEndDay;
        if (index === 0 && index !== mappedData?.length - 1) {
          iterationStartDay = new Date(dateFrom)?.getDate();
          iterationEndDay = new Date(iterationYear, iterationMonth, 0)?.getDate();
        } else if (index !== 0 && index === mappedData?.length - 1) {
          iterationStartDay = 1;
          iterationEndDay = new Date(dateTo)?.getDate();
        } else if (index === 0 && index === mappedData?.length - 1) {
          iterationStartDay = new Date(dateFrom)?.getDate();
          iterationEndDay = new Date(dateTo)?.getDate();
        } else {
          iterationStartDay = 1;
          iterationEndDay = new Date(iterationYear, iterationMonth, 0)?.getDate();
        }
        const ITERATION_NUMBER_OF_DAYS = iterationEndDay - iterationStartDay + 1;

        // console.log('ITERATION: ', {
        //   startDay: iterationStartDay,
        //   endDay: iterationEndDay,
        //   iterationYear,
        //   iterationMonth,
        //   ITERATION_NUMBER_OF_DAYS
        // });

        //
        let sheet;
        if (index === 0) {
          sheet = workbook.sheet(0).name(t('monthlyPlan.' + iterationMonth) + ' ' + iterationYear)
        } else {
          sheet = workbook.addSheet(t('monthlyPlan.' + iterationMonth) + ' ' + iterationYear);
        }

        const lastColumn = getExcelColumnLabel(ITERATION_NUMBER_OF_DAYS + 2);

        sheet.range('A2:' + lastColumn + '2').merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'thick',
          horizontalAlignment: 'center',
          bold: true
        })

        sheet.column('A').width(20);
        for (let i = 2; i <= ITERATION_NUMBER_OF_DAYS + 1; i += 1) {
          const columnLabel = getExcelColumnLabel(i);
          sheet.column(columnLabel).width(4).style({
            horizontalAlignment: 'center'
          });
        }

        sheet.cell('A2').value('OKL mjesečna evidencija sati po danima i mjesečni zbir');

        sheet.cell('A3').value(mapHeadingRows(iterationMonth, iterationYear, iterationStartDay, iterationEndDay, 'MONTHLY'));
        sheet.range('A3:' + lastColumn + '3').style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A4:' + lastColumn + '4').style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + '3:' + lastColumn + '4').value('Zbir:').merged(true).style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });
        sheet.cell('A3').style({
          horizontalAlignment: 'center'
        });
        sheet.cell('A4').style({
          horizontalAlignment: 'center'
        });

        sheet.cell('A5').value(sectorRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        // ASSE SECTION
        const asseSectionStartRow = 5 + sectorRows?.length;
        sheet.range('A' + asseSectionStartRow + ':' + lastColumn + asseSectionStartRow).merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'medium',
          horizontalAlignment: 'center',
          bold: true
        });

        sheet.cell('A' + asseSectionStartRow).value('ASSE mjesečna evidencija sati po danima i mjesečni zbir');
        sheet.cell('A' + (asseSectionStartRow + 1)).value(mapHeadingRows(iterationMonth, iterationYear, iterationStartDay, iterationEndDay, 'MONTHLY'));
        sheet.range('A' + (asseSectionStartRow + 1) + ':' + lastColumn + (asseSectionStartRow + 1)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A' + (asseSectionStartRow + 2) + ':' + lastColumn + (asseSectionStartRow + 2)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + (asseSectionStartRow + 1) + ':' + lastColumn + (asseSectionStartRow + 2)).merged(true).value('Zbir:').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });

        sheet.cell('A' + (asseSectionStartRow + 3)).value(asseRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        sheet.cell('A' + (asseSectionStartRow + 1)).style({ horizontalAlignment: 'center' });
        sheet.cell('A' + (asseSectionStartRow + 2)).style({ horizontalAlignment: 'center' });

        // OJTI SECTION
        const ojtiSectionStartRow = asseSectionStartRow + asseRows?.length + 3;
        sheet.range('A' + ojtiSectionStartRow + ':' + lastColumn + ojtiSectionStartRow).merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'medium',
          horizontalAlignment: 'center',
          bold: true
        });

        sheet.cell('A' + ojtiSectionStartRow).value('OJTI');
        sheet.cell('A' + (ojtiSectionStartRow + 1)).value(mapHeadingRows(iterationMonth, iterationYear, iterationStartDay, iterationEndDay, 'MONTHLY'));
        sheet.range('A' + (ojtiSectionStartRow + 1) + ':' + lastColumn + (ojtiSectionStartRow + 1)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A' + (ojtiSectionStartRow + 2) + ':' + lastColumn + (ojtiSectionStartRow + 2)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + (ojtiSectionStartRow + 1) + ':' + lastColumn + (ojtiSectionStartRow + 2)).merged(true).value('Zbir:').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });

        sheet.cell('A' + (ojtiSectionStartRow + 3)).value(ojtiRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        sheet.cell('A' + (ojtiSectionStartRow + 1)).style({ horizontalAlignment: 'center' });
        sheet.cell('A' + (ojtiSectionStartRow + 2)).style({ horizontalAlignment: 'center' });

        // MANAGER SECTION
        const managerSectionStartRow = ojtiSectionStartRow + ojtiRows?.length + 3;
        sheet.range('A' + managerSectionStartRow + ':' + lastColumn + managerSectionStartRow).merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'medium',
          horizontalAlignment: 'center',
          bold: true
        });

        sheet.cell('A' + managerSectionStartRow).value(atcu === 'ATCU1' ? 'Šefovi smjena' : 'SATCO');
        sheet.cell('A' + (managerSectionStartRow + 1)).value(mapHeadingRows(iterationMonth, iterationYear, iterationStartDay, iterationEndDay, 'MONTHLY'));
        sheet.range('A' + (managerSectionStartRow + 1) + ':' + lastColumn + (managerSectionStartRow + 1)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A' + (managerSectionStartRow + 2) + ':' + lastColumn + (managerSectionStartRow + 2)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + (managerSectionStartRow + 1) + ':' + lastColumn + (managerSectionStartRow + 2)).merged(true).value('Zbir:').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });

        sheet.cell('A' + (managerSectionStartRow + 3)).value(managerRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        sheet.cell('A' + (managerSectionStartRow + 1)).style({ horizontalAlignment: 'center' });
        sheet.cell('A' + (managerSectionStartRow + 2)).style({ horizontalAlignment: 'center' });
      });

      if (mappedData?.length > 1) {
        const { sectorRows, asseRows, ojtiRows, managerRows } = totalData?.[0];
        const sheet = workbook.addSheet('Ukupno ' + dateFrom + ' - ' + dateTo);
        const lastColumn = 'B';

        sheet.range('A2:' + lastColumn + '2').merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'thick',
          horizontalAlignment: 'center',
          bold: true
        })

        sheet.column('A').width(20);

        sheet.cell('A2').value('OKL');

        sheet.cell('A3').value(mapHeadingRows(null, null, null, null, 'YEARLY'));
        sheet.range('A3:' + lastColumn + '3').style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A4:' + lastColumn + '4').style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + '3:' + lastColumn + '4').value('Zbir:').merged(true).style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });
        sheet.cell('A3').style({
          horizontalAlignment: 'center'
        });
        sheet.cell('A4').style({
          horizontalAlignment: 'center'
        });

        sheet.cell('A5').value(sectorRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        // ASSE SECTION
        const asseSectionStartRow = 5 + (sectorRows?.length || 0);
        sheet.range('A' + asseSectionStartRow + ':' + lastColumn + asseSectionStartRow).merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'medium',
          horizontalAlignment: 'center',
          bold: true
        });

        sheet.cell('A' + asseSectionStartRow).value('ASSE');
        sheet.cell('A' + (asseSectionStartRow + 1)).value(mapHeadingRows(null, null, null, null, 'YEARLY'));
        sheet.range('A' + (asseSectionStartRow + 1) + ':' + lastColumn + (asseSectionStartRow + 1)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A' + (asseSectionStartRow + 2) + ':' + lastColumn + (asseSectionStartRow + 2)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + (asseSectionStartRow + 1) + ':' + lastColumn + (asseSectionStartRow + 2)).merged(true).value('Zbir:').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });

        sheet.cell('A' + (asseSectionStartRow + 3)).value(asseRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        sheet.cell('A' + (asseSectionStartRow + 1)).style({ horizontalAlignment: 'center' });
        sheet.cell('A' + (asseSectionStartRow + 2)).style({ horizontalAlignment: 'center' });

        // OJTI SECTION
        const ojtiSectionStartRow = asseSectionStartRow + (asseRows?.length || 0) + 3;
        sheet.range('A' + ojtiSectionStartRow + ':' + lastColumn + ojtiSectionStartRow).merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'medium',
          horizontalAlignment: 'center',
          bold: true
        });

        sheet.cell('A' + ojtiSectionStartRow).value('OJTI');
        sheet.cell('A' + (ojtiSectionStartRow + 1)).value(mapHeadingRows(null, null, null, null, 'YEARLY'));
        sheet.range('A' + (ojtiSectionStartRow + 1) + ':' + lastColumn + (ojtiSectionStartRow + 1)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A' + (ojtiSectionStartRow + 2) + ':' + lastColumn + (ojtiSectionStartRow + 2)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + (ojtiSectionStartRow + 1) + ':' + lastColumn + (ojtiSectionStartRow + 2)).merged(true).value('Zbir:').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });

        sheet.cell('A' + (ojtiSectionStartRow + 3)).value(ojtiRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        sheet.cell('A' + (ojtiSectionStartRow + 1)).style({ horizontalAlignment: 'center' });
        sheet.cell('A' + (ojtiSectionStartRow + 2)).style({ horizontalAlignment: 'center' });

        // MANAGER SECTION
        const managerSectionStartRow = ojtiSectionStartRow + (ojtiRows?.length || 0) + 3;
        sheet.range('A' + managerSectionStartRow + ':' + lastColumn + managerSectionStartRow).merged(true).style({
          fill: 'ffc000',
          fontColor: 'ff0100',
          border: true,
          borderStyle: 'medium',
          horizontalAlignment: 'center',
          bold: true
        });

        sheet.cell('A' + managerSectionStartRow).value(atcu === 'ATCU1' ? 'Šefovi smjena' : 'SATCO');
        sheet.cell('A' + (managerSectionStartRow + 1)).value(mapHeadingRows(null, null, null, null, 'YEARLY'));
        sheet.range('A' + (managerSectionStartRow + 1) + ':' + lastColumn + (managerSectionStartRow + 1)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range('A' + (managerSectionStartRow + 2) + ':' + lastColumn + (managerSectionStartRow + 2)).style({
          fill: 'ff6600',
          border: true,
          borderStyle: 'medium'
        });
        sheet.range(lastColumn + (managerSectionStartRow + 1) + ':' + lastColumn + (managerSectionStartRow + 2)).merged(true).value('Zbir:').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center'
        });

        sheet.cell('A' + (managerSectionStartRow + 3)).value(managerRows).style({
          border: true,
          borderStyle: 'thin',
          bold: true,
          numberFormat: '0.0'
        });

        sheet.cell('A' + (managerSectionStartRow + 1)).style({ horizontalAlignment: 'center' });
        sheet.cell('A' + (managerSectionStartRow + 2)).style({ horizontalAlignment: 'center' });
      }

      return workbook.outputAsync().then((res) => {
        const fileName = `Mjesečna evidencija sati - ${atcu} - ${dateFrom} - ${dateTo}`;
        saveAs(res, fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    })
  };

  return {
    getHoursReport,
    fetchMonthlyHoursReportData,
    loading
  }
};

export default useHoursReportExport;
